/**
 * Image preloading for Custom Offer Unit step-through.
 *
 * Without this, an offer's image is only fetched once its markup lands in the
 * DOM — so stepping to the next offer shows text first and the image pops in a
 * beat later. Warming the browser cache ahead of time makes the image appear
 * together with the content on navigation.
 */
const requested = new Set();
const inflight = new Set();
const SKIP_SCHEME = /^(data|blob|javascript):/i;
/** Pull `<img src/srcset>`, `<source srcset>`, and inline `background-image` URLs. */
export function extractImageUrls(html) {
    if (!html || typeof document === 'undefined') {
        return [];
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    const urls = [];
    template.content.querySelectorAll('img[src]').forEach(img => {
        const src = img.getAttribute('src');
        if (src) {
            urls.push(src);
        }
    });
    template.content
        .querySelectorAll('img[srcset], source[srcset]')
        .forEach(el => {
        const srcset = el.getAttribute('srcset');
        if (!srcset) {
            return;
        }
        for (const candidate of srcset.split(',')) {
            const url = candidate.trim().split(/\s+/)[0];
            if (url) {
                urls.push(url);
            }
        }
    });
    template.content.querySelectorAll('[style]').forEach(el => {
        const style = el.getAttribute('style');
        if (!style) {
            return;
        }
        const re = /url\(\s*(['"]?)([^'")]+)\1\s*\)/g;
        let match;
        while ((match = re.exec(style)) !== null) {
            if (match[2]) {
                urls.push(match[2]);
            }
        }
    });
    return urls;
}
/** Warm the browser cache for the given image URLs (deduped, fire-and-forget). */
export function preloadImages(urls) {
    if (typeof Image === 'undefined') {
        return;
    }
    for (const raw of urls) {
        const url = raw?.trim();
        if (!url || requested.has(url) || SKIP_SCHEME.test(url)) {
            continue;
        }
        requested.add(url);
        const img = new Image();
        img.decoding = 'async';
        const settle = () => {
            inflight.delete(img);
        };
        img.onload = settle;
        img.onerror = settle;
        inflight.add(img);
        img.src = url;
    }
}
